// Shared chrome for the billing overview dashboard: content card and KPI stat.

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';

// The 'i' next to a card/KPI title: hover (or focus) explains what the
// number or chart actually measures.
export function InfoHint({ text }: { text: string }) {
    return (
        <Tooltip content={text} focusable>
            <span className="text-gray-300 hover:text-gray-500 transition-colors cursor-help inline-flex shrink-0">
                <InformationCircleIcon className="h-4 w-4" />
            </span>
        </Tooltip>
    );
}

interface OverviewCardProps {
    title: React.ReactNode;
    // Hover explanation of what the card's data represents.
    info?: string;
    // Muted line under the title, e.g. the period a card covers; a node replaces the whole line.
    subtitle?: React.ReactNode;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    // When true, the content area gets no inner padding (e.g. a table that
    // wants to run its own rows edge-to-edge) -- the header keeps its own.
    noPadding?: boolean;
    // Extra classes for the content wrapper -- e.g. `flex-1 min-h-0` so a card can
    // stretch to fill a fixed-height parent instead of sizing to its content.
    contentClassName?: string;
    // Overrides the default title size, for pages wanting a heavier heading.
    titleClassName?: string;
    // Drops the action to its own row once the title column would shrink under 8rem.
    wrapAction?: boolean;
}

export function OverviewCard({
    title,
    info,
    subtitle,
    action,
    children,
    className = '',
    noPadding = false,
    contentClassName = '',
    titleClassName = 'text-sm',
    wrapAction = false
}: OverviewCardProps) {
    const heading = (
        <h3
            className={`font-bold text-gray-800 flex items-center gap-1.5 min-w-0 ${titleClassName}`}
        >
            {title}
            {info && <InfoHint text={info} />}
        </h3>
    );
    return (
        <div
            className={`bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] min-w-0 ${className}`}
        >
            <div
                className={`flex items-center justify-between ${wrapAction ? 'flex-wrap gap-y-2' : ''} ${noPadding ? 'px-5 pt-5 pb-3' : 'p-5 pb-0 mb-4'}`}
            >
                {subtitle ? (
                    <div
                        className={`flex min-w-0 flex-1 flex-col gap-0.5 ${wrapAction ? 'basis-32' : ''}`}
                    >
                        {heading}
                        {typeof subtitle === 'string' ? (
                            <span className="truncate text-xs text-gray-500">{subtitle}</span>
                        ) : (
                            subtitle
                        )}
                    </div>
                ) : (
                    heading
                )}
                {action}
            </div>
            <div className={`${noPadding ? '' : 'px-5 pb-5'} ${contentClassName}`}>{children}</div>
        </div>
    );
}

export type KpiDeltaTone = 'up' | 'down' | 'neutral';

interface KpiCardProps {
    label: string;
    // Hover explanation of what the stat measures.
    info?: string;
    value: React.ReactNode;
    delta?: string | null;
    deltaTone?: KpiDeltaTone;
    foot?: React.ReactNode;
    // Rendered under the foot, e.g. a small trend chart.
    children?: React.ReactNode;
}

const DELTA_CLASSES: Record<KpiDeltaTone, string> = {
    up: 'text-green-600',
    down: 'text-red-500',
    neutral: 'text-gray-400'
};

export function KpiCard({
    label,
    info,
    value,
    delta,
    deltaTone = 'neutral',
    foot,
    children
}: KpiCardProps) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] px-5 py-4 flex flex-col gap-1.5 min-w-0">
            <span className="text-[12px] font-medium text-gray-500 flex items-center gap-1">
                {label}
                {info && <InfoHint text={info} />}
            </span>
            <div className="flex items-end gap-2 flex-wrap">
                <span className="text-[26px] leading-none font-bold text-gray-900 tabular-nums">
                    {value}
                </span>
                {delta && (
                    <span className={`text-xs font-semibold mb-0.5 ${DELTA_CLASSES[deltaTone]}`}>
                        {delta}
                    </span>
                )}
            </div>
            {foot && <span className="truncate text-[11px] text-gray-400">{foot}</span>}
            {children}
        </div>
    );
}
